function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import _ from 'lodash';
import { ApolloLink, Observable } from '@apollo/client';
import { getEnvironment } from '@sail/utils';
import { MAX_INCLUDE_ONLY } from '@stripe-internal/babel-plugin-data-graphql-helpers';
import { RestLink as ApolloRestLink } from '../../forked/apollo-link-rest';
import { bodyBuilder, runtimePathBuilder } from './builders';
import RestLinkError from './RestLinkError';
import getPossibleTypes from '../../utils/getPossibleTypes';
import stringify from './stringify';
import queriesToExecuteUsingPost from './queriesToExecuteUsingPost';
import { rebuildResponse } from './errorConversion';
import { parseQueryParams } from './parseQueryParams';
const convertValue = (unionObjectTypesMap, possibleTypes, value, converter)=>{
    if (_.isPlainObject(value)) {
        return converter(unionObjectTypesMap, possibleTypes, value);
    }
    if (_.isArray(value)) {
        return value.map((v)=>converter(unionObjectTypesMap, possibleTypes, v));
    }
    return value;
};
/*
 * Takes a response data object and recursively:
 * 1. Checks if it has an `object` field
 * 2. Checks `__typename` to see if it's a Union type
 * 3. Looks up unionObjectTypesMap to get the possible list of object types for the union
 * 4. Gets the discrete GraphQL type for the object type.
 * 5. Checks if the GraphQL type from step 4. is a member of the Union
 * 6. Checks if the object is also deletable resource
 * 7. Replaces `__typename` with the discrete GraphQL type.
 */ const mapUnionTypenames = (unionObjectTypesMap, possibleTypes, data)=>{
    // We recursively call `mapUnionTypenames` on arrays and some arrays
    // may be partially populated with nulls, so we defensively check and
    // return null rather than trying to expand.
    if (!data) {
        return data;
    }
    const { object, deleted, __typename } = data;
    if (possibleTypes[__typename]) {
        if (object == null) {
            // Skip union type narrowing for data with only an `id`, and just
            // leave the union typename (go/skip-id-only-expands)
            const fields = Object.keys(data);
            if (fields.every((field)=>field === 'id' || field === '__typename')) {
                // Hack -- Apollo requires a valid typename for the field, so just
                // return the first one.
                data.__typename = possibleTypes[__typename][0];
                return data;
            }
            throw new RestLinkError(`The union type "${__typename}" requires an \`object\` property but data was provided without it`);
        }
        const unionObjectTypes = unionObjectTypesMap[__typename];
        const objectTypes = unionObjectTypes ? // This should not happen, but for now, treat them as the same object: 'charge' so
        // search results can be rendered without error. See RUN_SEARCH-9353.
        unionObjectTypes[object === 'payment' ? 'charge' : object] : null;
        if (objectTypes) {
            const [resource, deletedResource] = objectTypes;
            const typename = deleted ? deletedResource : resource;
            if (possibleTypes[__typename].includes(typename)) {
                data.__typename = typename;
            }
        }
    }
    if (typeof data !== 'object') {
        return data;
    }
    return _.isArray(data) ? data.map((value)=>convertValue(unionObjectTypesMap, possibleTypes, value, mapUnionTypenames)) : Object.keys(data).reduce((acc, key)=>_object_spread_props(_object_spread({}, acc), {
            [key]: convertValue(unionObjectTypesMap, possibleTypes, data[key], mapUnionTypenames)
        }), {});
};
const defaultSerializer = (data, headers)=>{
    return {
        body: stringify(data),
        headers
    };
};
/**
 * This is required to correctly handle fragments on union types, particularly where union members have fields with
 * the same name but incompatible types.
 *
 * It returns `true` to execute a fragment on type `typeCondition` against `rootValue`, a data object. Any executed
 * fragments are merged. By default all fragments are executed against all data; see:
 * https://github.com/apollographql/apollo-client/tree/64cda6af5ebd1b917d8838790e043cc70dab4ed2/packages/graphql-anywhere#options
 *
 * Note that this matcher in particular will execute both fragments for a union of a deleted and not-deleted type,
 * because they have the same `object`.
 *
 * Here's a relevant issue that involves a similar solution to ours:
 * - https://github.com/apollographql/apollo-client/issues/3802
 * I think this might no longer be needed with Apollo Client 3; here is a relevant issue:
 * - https://github.com/apollographql/apollo-client/issues/6632
 * - possibleTypes: https://www.apollographql.com/docs/react/data/fragments/#using-fragments-with-unions-and-interfaces
 */ const fragmentMatcher = (unionObjectTypesMap, rootValue, typeCondition)=>{
    const unionTypenames = unionObjectTypesMap[rootValue.__typename];
    if (!unionTypenames) {
        return true; // fail open
    }
    const typenamesForObject = unionTypenames[rootValue.object];
    if (!typenamesForObject) {
        return true; // fail open
    }
    if (Array.isArray(typenamesForObject)) {
        return typenamesForObject.includes(typeCondition);
    }
    return false;
};
class RestLink extends ApolloLink {
    request(operation, forward) {
        /**
     * Inject the pathBuilder as a variable for use with apollo-link-rest,
     * which is converting some of our gql queries into rest calls, and need
     * pathBuilder to construct the correct API path. Direct setting
     * pathBuilders on operation.variables does not work, we must assign a new
     * object.
     */ operation.variables = _object_spread_props(_object_spread({}, operation.variables || {}), {
            pathBuilder: (params)=>{
                var _this_queryEndpointsToBeRequestedWithPost, _params_args, _this_shadowComparisonOperationsV1ToV2;
                const { headers, observabilityFns, queryProject } = operation.getContext();
                if (queriesToExecuteUsingPost.has(params['@rest'].runtimePath)) {
                    params['@rest'].method = 'POST';
                }
                const { mainDefinition, fragmentDefinitions } = params.context;
                /**
         * If the apiKey V1ContextInput property is specified, we should check
         * whether the API key is a publishable key. If it is, providing the
         * `include_only` request param will cause the API call to fail as it
         * works for dashboard_only API keys only.
         */ const isDashboardOnlyAPIKey = !((headers === null || headers === void 0 ? void 0 : headers.Authorization) && ((headers === null || headers === void 0 ? void 0 : headers.Authorization.startsWith('Bearer pk_')) || (headers === null || headers === void 0 ? void 0 : headers.Authorization.startsWith('Bearer ek_'))));
                const convertGetQueryToPostBodyForRequest = params['@rest'].method === 'GET' && ((_this_queryEndpointsToBeRequestedWithPost = this.queryEndpointsToBeRequestedWithPost) === null || _this_queryEndpointsToBeRequestedWithPost === void 0 ? void 0 : _this_queryEndpointsToBeRequestedWithPost.includes(params['@rest'].runtimePath));
                var _params_args_input;
                let path = runtimePathBuilder(operation.operationName, params['@rest'], mainDefinition, fragmentDefinitions, (_params_args_input = (_params_args = params.args) === null || _params_args === void 0 ? void 0 : _params_args.input) !== null && _params_args_input !== void 0 ? _params_args_input : {}, isDashboardOnlyAPIKey, observabilityFns, convertGetQueryToPostBodyForRequest);
                if (convertGetQueryToPostBodyForRequest) {
                    params['@rest'].method = 'POST';
                    const [basePath, queryString] = path.split('?');
                    // Remove the query string from the path
                    path = basePath;
                    // ApolloLink builds the body from input. We fill it with the contents of the query string
                    params.args.input = parseQueryParams(queryString);
                }
                if (params['@rest'].method === 'GET' && path.length >= MAX_INCLUDE_ONLY) {
                    if (getEnvironment() === 'development') {
                        console.warn('V1 GraphQL request using GET method approaching maximum URL size', {
                            endpoint: params['@rest'].runtimePath,
                            project: queryProject,
                            operationName: operation.operationName,
                            size: path.length
                        });
                    }
                    // Report to analytics when a GET request has a too long URL path.
                    // If you're investigating such error for your query, please make the
                    // query endpoint accept POST requests and add the endpoint to the
                    // queriesToExecUsingPost.tsx file.
                    //
                    // With this, the query will be executed using POST, and the limit that
                    // some browsers have around long URLs will be bypassed.
                    observabilityFns === null || observabilityFns === void 0 ? void 0 : observabilityFns.analytics.track('v1_graphql_request_with_long_url', {
                        endpoint: params['@rest'].runtimePath,
                        project: queryProject,
                        operationName: operation.operationName,
                        size: path.length
                    });
                }
                /**
         * Create a map where each path returned from `pathBuilder` is the key,
         * and the tokenized path minus the query string (e.g.
         * `/ajax/logs/:id`) is the value.
         *
         * We use a map because each operation may contain more than one REST
         * request.
         *
         * We use this later to send metrics with the tokenized path to keep
         * cardinality low.
         */ operation.setContext(({ paths })=>{
                    var _params_rest;
                    return {
                        paths: _object_spread_props(_object_spread({}, paths || {}), {
                            [`/${path}`]: (_params_rest = params['@rest']) === null || _params_rest === void 0 ? void 0 : _params_rest.runtimePath
                        })
                    };
                });
                if ((_this_shadowComparisonOperationsV1ToV2 = this.shadowComparisonOperationsV1ToV2) === null || _this_shadowComparisonOperationsV1ToV2 === void 0 ? void 0 : _this_shadowComparisonOperationsV1ToV2[operation.operationName]) {
                    operation.setContext((prevCtx)=>_object_spread_props(_object_spread({}, prevCtx), {
                            underlyingRequest: params
                        }));
                }
                return path;
            },
            bodyBuilder
        });
        const errorsCache = new WeakMap();
        /**
     * Map the Observable that apollo-link-rest returns through
     * `mapUnionTypenames`.
     */ return new Observable((observer)=>{
            var _this_restLink_request;
            const result = (_this_restLink_request = this.restLink.request(operation, forward)) === null || _this_restLink_request === void 0 ? void 0 : _this_restLink_request.map((res)=>{
                var _response_errors;
                let response = res;
                // @ts-expect-error Error types are right.
                response = rebuildResponse(response);
                if ((_response_errors = response.errors) === null || _response_errors === void 0 ? void 0 : _response_errors.length) {
                    errorsCache.set(response.errors[0], response.errors);
                    throw response.errors[0];
                }
                const data = mapUnionTypenames(this.unionObjectTypesMap, this.possibleTypes, response.data);
                return {
                    data,
                    errors: response.errors
                };
            });
            if (result) {
                result.subscribe({
                    next: function onNextRestLink(value) {
                        observer.next(value);
                        observer.complete();
                    },
                    error: function onErrorRestLink(value) {
                        const errors = (errorsCache.get(value) || [
                            value
                        ]).filter(Boolean);
                        observer.next({
                            data: null,
                            errors: errors.length ? errors : undefined
                        });
                        observer.complete();
                    }
                });
            }
        });
    }
    constructor(unionObjectTypesMap, queryEndpointsToBeRequestedWithPost, shadowComparisonOperationsV1ToV2){
        super();
        _define_property(this, "restLink", void 0);
        _define_property(this, "unionObjectTypesMap", void 0);
        _define_property(this, "queryEndpointsToBeRequestedWithPost", void 0);
        _define_property(this, "possibleTypes", void 0);
        _define_property(this, "shadowComparisonOperationsV1ToV2", void 0);
        this.unionObjectTypesMap = unionObjectTypesMap;
        this.queryEndpointsToBeRequestedWithPost = queryEndpointsToBeRequestedWithPost;
        this.possibleTypes = getPossibleTypes(unionObjectTypesMap);
        this.shadowComparisonOperationsV1ToV2 = shadowComparisonOperationsV1ToV2;
        this.restLink = new ApolloRestLink({
            uri: '/',
            customFetch: undefined,
            defaultSerializer,
            fragmentMatcher: fragmentMatcher.bind(undefined, this.unionObjectTypesMap)
        });
    }
}
export { RestLink as default };

//# sourceMappingURL=RestLink.js.map